import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { createAgent, deleteAgent, updateAgent } from "../lib/api";
import { accentHex } from "../lib/theme";

const ACCENTS = ["coral", "teal", "purple", "amber", "rose", "emerald", "blue", "pink"];
const LANGUAGES = ["English", "Hindi", "Portuguese", "Japanese", "German", "Spanish", "French", "Mandarin", "Arabic"];

export default function AgentEditorModal({ mode, agent, agents, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [name, setName] = useState(agent?.name || "");
  const [group, setGroup] = useState(agent?.group || "projects");
  const [parentId, setParentId] = useState(agent?.parent_id || "");
  const [accentColor, setAccentColor] = useState(agent?.accent_color || "teal");
  const [language, setLanguage] = useState(agent?.language || "English");
  const [geography, setGeography] = useState(agent?.geography || "");
  const [targetLeads, setTargetLeads] = useState(agent?.target_leads || "");
  const [brief, setBrief] = useState(agent?.brief || "");
  const [saving, setSaving] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // parent options: top-level agents in same group, excluding self
  const parentOptions = useMemo(() => {
    return agents.filter(
      (a) =>
        !a.parent_id &&
        a.group === group &&
        (!isEdit || a.id !== agent?.id) &&
        // can't be parent if target agent already has children
        !(isEdit && agents.some((c) => c.parent_id === agent?.id)),
    );
  }, [agents, group, agent, isEdit]);

  // If editing a top-level agent that has children, parent must stay null
  const lockedAsParent = isEdit && agents.some((c) => c.parent_id === agent?.id);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !saving && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        group,
        accent_color: accentColor,
        language: language.trim() || "English",
        geography: geography.trim(),
        target_leads: targetLeads.trim(),
        brief: brief.trim(),
      };
      if (isEdit) {
        if (!parentId) {
          payload.clear_parent = true;
        } else {
          payload.parent_id = parentId;
        }
        const updated = await updateAgent(agent.id, payload);
        toast.success("Agent updated");
        onSaved(updated, "update");
      } else {
        if (parentId) payload.parent_id = parentId;
        const created = await createAgent(payload);
        toast.success("Agent created");
        onSaved(created, "create");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    setSaving(true);
    try {
      await deleteAgent(agent.id);
      toast.success(`${agent.name} deleted`);
      onSaved(agent, "delete");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-aot-bg/80 backdrop-blur-sm flex items-center justify-center p-4 aot-fade-in"
      onClick={() => !saving && onClose()}
      data-testid="agent-editor-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl bg-aot-surface border border-aot-border max-h-[92vh] overflow-hidden flex flex-col"
      >
        <div className="flex items-start justify-between p-6 border-b border-aot-border">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-aot-muted mb-2">
              {isEdit ? "Edit agent" : "New agent"}
            </div>
            <h3 className="font-display text-xl font-semibold tracking-tight">
              {isEdit ? agent.name : "Configure a new agent"}
            </h3>
          </div>
          <button
            type="button"
            onClick={() => !saving && onClose()}
            className="text-aot-muted hover:text-aot-text p-1"
            data-testid="agent-editor-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <Field label="Name" required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="agent-name-input"
              placeholder="e.g. Mental Health Podcast"
              className="input-dark"
              autoFocus
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Group">
              <select
                value={group}
                onChange={(e) => {
                  setGroup(e.target.value);
                  setParentId("");
                }}
                data-testid="agent-group-select"
                className="input-dark"
                disabled={lockedAsParent}
              >
                <option value="projects">Projects & Apps</option>
                <option value="fundraising">Fundraising</option>
              </select>
            </Field>
            <Field label="Parent agent (optional)">
              <select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                data-testid="agent-parent-select"
                className="input-dark"
                disabled={lockedAsParent}
                title={lockedAsParent ? "This agent has sub-agents and can't be nested" : ""}
              >
                <option value="">— None (top-level) —</option>
                {parentOptions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Accent colour">
            <div className="flex items-center gap-2 flex-wrap">
              {ACCENTS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setAccentColor(c)}
                  data-testid={`agent-accent-${c}`}
                  className={[
                    "w-8 h-8 border-2 transition-all",
                    accentColor === c ? "border-aot-text scale-110" : "border-aot-border hover:border-aot-muted",
                  ].join(" ")}
                  style={{ backgroundColor: accentHex(c) }}
                  title={c}
                  aria-label={c}
                />
              ))}
            </div>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Outreach language">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                data-testid="agent-language-select"
                className="input-dark"
              >
                {LANGUAGES.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Geography">
              <input
                value={geography}
                onChange={(e) => setGeography(e.target.value)}
                placeholder="e.g. India, Global, Singapore"
                data-testid="agent-geography-input"
                className="input-dark"
              />
            </Field>
          </div>

          <Field label="Target leads (short summary)">
            <input
              value={targetLeads}
              onChange={(e) => setTargetLeads(e.target.value)}
              placeholder="e.g. Mental health counsellors, podcast editors"
              data-testid="agent-target-input"
              className="input-dark"
            />
          </Field>

          <Field
            label="Brief (what Claude should search for)"
            hint="Be specific — this is the exact instruction Claude will use when searching the web."
          >
            <textarea
              value={brief}
              onChange={(e) => setBrief(e.target.value)}
              rows={6}
              data-testid="agent-brief-textarea"
              placeholder="Find real, verified leads for [project]. Target: (1) ... (2) ... (3) ..."
              className="input-dark resize-y"
            />
          </Field>
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-aot-border">
          {isEdit ? (
            confirmingDelete ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-aot-rose font-mono uppercase tracking-wider">
                  Delete + all leads?
                </span>
                <button
                  type="button"
                  onClick={onDelete}
                  disabled={saving}
                  data-testid="agent-confirm-delete-button"
                  className="px-3 py-1.5 bg-aot-rose/90 text-white text-xs font-mono uppercase tracking-wider hover:bg-aot-rose disabled:opacity-50"
                >
                  Yes, delete
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(false)}
                  disabled={saving}
                  className="px-3 py-1.5 border border-aot-border text-xs font-mono uppercase tracking-wider text-aot-muted"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                disabled={saving}
                data-testid="agent-delete-button"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-wider text-aot-rose hover:bg-aot-rose/10 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete agent
              </button>
            )
          ) : (
            <span className="text-[10px] font-mono uppercase tracking-wider text-aot-subtle">
              Agents run on demand
            </span>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => !saving && onClose()}
              className="px-4 py-2 text-xs font-mono uppercase tracking-wider text-aot-muted hover:text-aot-text"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="agent-save-button"
              className="inline-flex items-center gap-2 px-5 py-2 bg-aot-teal text-aot-bg text-xs font-mono uppercase tracking-wider hover:opacity-90 disabled:opacity-40"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {isEdit ? "Save changes" : "Create agent"}
            </button>
          </div>
        </div>
      </form>
      <style>{`
        .input-dark {
          width: 100%;
          background: rgb(var(--aot-bg));
          border: 1px solid rgb(var(--aot-border));
          padding: 0.5rem 0.75rem;
          font-size: 0.875rem;
          color: rgb(var(--aot-text));
          transition: border-color 150ms;
        }
        .input-dark:focus {
          outline: none;
          border-color: #2DD4BF;
        }
        .input-dark::placeholder { color: rgb(var(--aot-subtle)); }
        .input-dark:disabled { opacity: 0.5; cursor: not-allowed; }
        select.input-dark option { background: rgb(var(--aot-surface)); color: rgb(var(--aot-text)); }
      `}</style>
    </div>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-mono uppercase tracking-[0.25em] text-aot-muted mb-2">
        {label}
        {required && <span className="text-aot-rose ml-1">*</span>}
      </span>
      {children}
      {hint && <span className="block text-[11px] text-aot-subtle mt-1.5">{hint}</span>}
    </label>
  );
}
